"use client";

/**
 * Generic feature-page acquisition CTA (ticket G3-9).
 *
 * This is the source-parameterised version that supersedes the per-feature
 * `rams-*` / `cpp-*` tracked CTAs introduced in G3-2 / G3-3. Those earlier
 * components hard-coded their PostHog event names per feature; this one takes a
 * `source` string instead, so a single component serves every feature page.
 * The RAMS / CPP CTAs can migrate to this once their PRs merge.
 *
 * Behaviour mirrors the established client-capture pattern: fire the PostHog
 * event on click, then let navigation proceed. It is fire-and-forget — it must
 * NEVER call preventDefault, so analytics can never block or delay the signup
 * journey.
 */

import Link from "next/link";
import { useCallback } from "react";
import { usePostHog } from "posthog-js/react";

interface FeatureTrackedCtaProps {
  href: string;
  className: string;
  children: React.ReactNode;
  /** PostHog event source, e.g. "features_coshh". */
  source: string;
  /** Where on the page, e.g. "hero" | "final_cta" | "starter_vs_pro". */
  location: string;
  /** Capture event name. */
  event: "feature_cta_clicked" | "feature_sample_viewed";
}

export function FeatureTrackedCta({
  href,
  className,
  children,
  source,
  location,
  event,
}: FeatureTrackedCtaProps) {
  const posthog = usePostHog();

  const handleClick = useCallback(() => {
    // Fire-and-forget: capture, then let the <Link> navigate. Never
    // preventDefault — analytics must not gate the navigation.
    posthog?.capture?.(event, { source, location });
  }, [posthog, event, source, location]);

  return (
    <Link href={href} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
}
